import { GridFilterModel, getDefaultGridFilterModel } from '@mui/x-data-grid';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	addExcludedFilter,
	addPolygonFilter,
	hasExcludedFilter,
	removeExcludedFilter,
} from '../../utils/filter';
import { moduleNamespace } from '../AssetGrid/redux/store.config';
import { DefaultRootState } from 'react-redux';

export interface FilterState {
	rawFilter: GridFilterModel;
	polygonFilter: GeoJSON.Feature<GeoJSON.Polygon> | undefined;
	excludedList: string[];
	selectedSystemType: string | undefined;
	selectedAssetType: string | undefined;
}

const initialState: FilterState = {
	rawFilter: getDefaultGridFilterModel(),
	polygonFilter: undefined,
	excludedList: [],
	selectedSystemType: undefined,
	selectedAssetType: undefined,
};

const filterSlice = createSlice({
	name: 'filter',
	initialState,
	reducers: {
		setPolygonFilter(
			state,
			action: PayloadAction<GeoJSON.Feature<GeoJSON.Polygon> | undefined>,
		) {
			state.polygonFilter = action.payload;
			state.rawFilter = addPolygonFilter(
				state.rawFilter as GridFilterModel,
				action.payload,
			) as typeof state.rawFilter;
		},
		setFilterModel(state, action: PayloadAction<GridFilterModel>) {
			const newModel = action.payload;
			// If excluded filter was removed from model but excludedList is non-empty, clear list
			if (
				state.excludedList.length > 0 &&
				hasExcludedFilter(state.rawFilter as GridFilterModel) &&
				!hasExcludedFilter(newModel)
			) {
				state.excludedList = [];
			}
			state.rawFilter = newModel as typeof state.rawFilter;
		},
		addExcludedItem(state, action: PayloadAction<string>) {
			if (!state.excludedList.includes(action.payload)) {
				state.excludedList.push(action.payload);
			}
			state.rawFilter = addExcludedFilter(
				state.rawFilter as GridFilterModel,
			) as typeof state.rawFilter;
		},
		removeExcludedItem(state, action: PayloadAction<string>) {
			state.excludedList = state.excludedList.filter(
				id => id !== action.payload,
			);
			if (state.excludedList.length === 0) {
				state.rawFilter = removeExcludedFilter(
					state.rawFilter as GridFilterModel,
				) as typeof state.rawFilter;
			}
		},
		clearFilters(state) {
			state.rawFilter = getDefaultGridFilterModel() as typeof state.rawFilter;
			state.excludedList = [];
			state.polygonFilter = undefined;
		},
		setSelectedAssetView(
			state,
			action: PayloadAction<{ systemType: string; assetType: string }>,
		) {
			state.selectedSystemType = action.payload.systemType;
			state.selectedAssetType = action.payload.assetType;
		},
		setSelectedSystemType(state, action: PayloadAction<string>) {
			state.selectedSystemType = action.payload;
			state.selectedAssetType = undefined;
		},
		setSelectedAssetType(state, action: PayloadAction<string>) {
			state.selectedAssetType = action.payload;
		},
	},
});

export const {
	setPolygonFilter: setPolygonFilterAction,
	setFilterModel: setFilterModelAction,
	addExcludedItem: addExcludedItemAction,
	removeExcludedItem: removeExcludedItemAction,
	clearFilters: clearFiltersAction,
	setSelectedAssetView: setSelectedAssetViewAction,
	setSelectedSystemType: setSelectedSystemTypeAction,
	setSelectedAssetType: setSelectedAssetTypeAction,
} = filterSlice.actions;

export default filterSlice.reducer;

// Selectors
const selectFilterBase = (state: DefaultRootState) =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(state as any)[moduleNamespace]?.filter as FilterState | undefined;

export const selectRawFilter = (state: DefaultRootState): GridFilterModel =>
	(selectFilterBase(state)?.rawFilter as GridFilterModel) ??
	getDefaultGridFilterModel();

export const selectPolygonFilter = (
	state: DefaultRootState,
): GeoJSON.Feature<GeoJSON.Polygon> | undefined =>
	selectFilterBase(state)?.polygonFilter;

export const selectExcludedList = (state: DefaultRootState): string[] =>
	selectFilterBase(state)?.excludedList ?? [];

export const selectSelectedSystemType = (
	state: DefaultRootState,
): string | undefined => selectFilterBase(state)?.selectedSystemType;

export const selectSelectedAssetType = (
	state: DefaultRootState,
): string | undefined => selectFilterBase(state)?.selectedAssetType;
